import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { useConfig } from '@/app/ConfigProvider';
import { setAuthHeaderProvider } from '@/services/api';
import type { User, AuthState } from '@/types/auth';
import { setTrackedUser } from '@/lib/error-tracking';

interface AuthContextValue extends AuthState {
  login: (user: User, token: string) => void;
  logout: () => void;
  getAuthHeaders: () => Record<string, string>;
  getStreamAuthHeaders: () => Record<string, string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

const STORAGE_KEY = 'valuegraph-auth';
const EXPIRY_BUFFER_MS = 60_000;

function getTokenExpiry(token: string): number | null {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  const expiry = getTokenExpiry(token);
  if (!expiry) return false;
  return Date.now() < expiry - EXPIRY_BUFFER_MS;
}

const LOGGED_OUT: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  accessToken: null,
};

function loadStoredAuth(): AuthState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return LOGGED_OUT;
    const parsed = JSON.parse(stored) as AuthState;
    if (!isTokenValid(parsed.accessToken)) {
      localStorage.removeItem(STORAGE_KEY);
      return LOGGED_OUT;
    }
    return { ...parsed, isLoading: false };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return LOGGED_OUT;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  useConfig();
  const queryClient = useQueryClient();

  const [state, setState] = useState<AuthState>(loadStoredAuth);

  // Server state is per-user, but the QueryClient is a module-level singleton
  // that outlives a sign-out — nothing remounts it without a page reload, and
  // staleTime means fresh entries aren't even refetched. So both ends of a
  // session boundary drop the cache: without this, the next person to sign in
  // on this browser sees the previous user's projects until the data goes
  // stale. Clearing on login as well covers signing in as someone else without
  // signing out first.
  const login = useCallback((user: User, token: string) => {
    const next: AuthState = {
      user,
      isAuthenticated: true,
      isLoading: false,
      accessToken: token,
    };
    queryClient.clear();
    setState(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, [queryClient]);

  const logout = useCallback(() => {
    setState(LOGGED_OUT);
    localStorage.removeItem(STORAGE_KEY);
    queryClient.clear();
  }, [queryClient]);

  // A lapsing token ends the session too, so it goes through logout() rather
  // than clearing the state inline — otherwise the "both ends of a session
  // boundary drop the cache" invariant above holds only for the explicit
  // paths, and a token that expires in a shared browser leaves one user's
  // cached projects for whoever signs in next. Declared after logout so the
  // dependency is initialised.
  useEffect(() => {
    if (!state.accessToken) return;
    const expiry = getTokenExpiry(state.accessToken);
    if (!expiry) return;

    const timeout = expiry - EXPIRY_BUFFER_MS - Date.now();
    if (timeout <= 0) {
      logout();
      return;
    }

    const timer = setTimeout(logout, timeout);

    return () => clearTimeout(timer);
  }, [state.accessToken, logout]);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    if (!state.accessToken || !isTokenValid(state.accessToken)) return {};
    return { Authorization: `Bearer ${state.accessToken}` };
  }, [state.accessToken]);

  // Streaming Function URLs sit behind CloudFront OAC, which claims the
  // Authorization header for its SigV4 signature — so the ID token rides in
  // x-id-token instead. Same token/validity check as getAuthHeaders.
  const getStreamAuthHeaders = useCallback((): Record<string, string> => {
    if (!state.accessToken || !isTokenValid(state.accessToken)) return {};
    return { 'x-id-token': state.accessToken };
  }, [state.accessToken]);

  useEffect(() => {
    setAuthHeaderProvider(getAuthHeaders);
  }, [getAuthHeaders]);

  // PM emails are data we already hold; respondents never authenticate, so
  // this can never tag a respondent.
  const trackedEmail = state.user?.email ?? null;
  useEffect(() => {
    setTrackedUser(trackedEmail);
  }, [trackedEmail]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, getAuthHeaders, getStreamAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
}
